import React, {useCallback, useEffect, useMemo, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Card,
  CardHeader,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {connect, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import FullscreenProvider, {useFullscreen} from "../../hocs/fullscreen-provider";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_INFO} from "../../hocs/snackbar-provider";
import {
  DEFAULT_GEOJSON_MAP_SETTINGS,
  DIMENSION_TYPE,
  getLabelField,
  getPointDataSelectorOptions,
  POINT_DATA_MAP_TYPE_CLUSTER,
  POINT_DATA_MAP_TYPE_HEATMAP
} from "./constants";
import IdentifyResults from "./IdentifyResults";
import {languageSelector} from "../../state/app/appSelectors";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import EXTRAS from "../../utils/extras";
import {getLayerOptions, getTranslationOptions, MAP_LAYERS_ALL} from "../../utils/map";
import "./style.css";

const LMap = window.LMap;

const mapStateToProps = state => {
  return {
    defaultLanguage: state.app.language,
    languages: state.app.languages,
    mapLayersConfig: state.app.mapLayersConfig,
    mapLayer: state.hub?.hub?.extras?.MapLayer || null,
    defaultBaseLayer: state.appConfig?.geojsonMapConfig?.baseLayer,
    defaultExtent: state.appConfig?.geojsonMapConfig?.defaultExtent,
    infoTolerance: state.appConfig?.geojsonMapConfig?.infoTolerance,
    opacity: state.appConfig?.geojsonMapConfig?.opacity,
    fillColor: state.appConfig?.geojsonMapConfig?.fillColor,
    borderColor: state.appConfig?.geojsonMapConfig?.borderColor,
    highlightedFillColor: state.appConfig?.geojsonMapConfig?.highlightedFillColor,
    highlightedBorderColor: state.appConfig?.geojsonMapConfig?.highlightedBorderColor,
    inactiveFillColor: state.appConfig?.geojsonMapConfig?.inactiveFillColor,
    inactiveBorderColor: state.appConfig?.geojsonMapConfig?.inactiveBorderColor,
    hoveredFillColor: state.appConfig?.geojsonMapConfig?.hoveredFillColor,
    hoveredLineColor: state.appConfig?.geojsonMapConfig?.hoveredLineColor,
    borderWidth: state.appConfig?.geojsonMapConfig?.borderWidth,
    borderDash: state.appConfig?.geojsonMapConfig?.borderDash,
    radius: state.appConfig?.geojsonMapConfig?.radius,
    sides: state.appConfig?.geojsonMapConfig?.sides,
    iconUrl: state.appConfig?.geojsonMapConfig?.iconUrl,
    heatmapGradient: state.appConfig?.geojsonMapConfig?.heatmapGradient,
    heatmapRadius: state.appConfig?.geojsonMapConfig?.heatmapRadius,
    heatmapBlur: state.appConfig?.geojsonMapConfig?.heatmapBlur,
    projDefsUrl: state.appConfig?.geojsonMapConfig?.projDefsUrl,
    labelFieldList: state.appConfig?.geojsonMapConfig?.labelFieldList,
    pointMapType: state.appConfig?.geojsonMapConfig?.pointMapType
  };
};

const GeoJsonMapComponent = ({
  defaultLanguage,
  languages,
  mapLayersConfig,
  mapLayer,
  defaultBaseLayer,
  defaultExtent,
  infoTolerance,
  opacity,
  fillColor,
  borderColor,
  highlightedFillColor,
  highlightedBorderColor,
  inactiveFillColor,
  inactiveBorderColor,
  hoveredFillColor,
  hoveredLineColor,
  borderWidth,
  borderDash,
  radius,
  sides,
  iconUrl,
  heatmapGradient,
  heatmapRadius,
  heatmapBlur,
  labelFieldList,
  pointMapType: pointMapTypeFromConfig,

  nodeId,
  showTitle = false,
  title,
  geoJson,
  infoMessage,
  labelField,
  disableBaseLayer = false,
  disableWheelZoom = false,
  disableFullscreen = false,
  leftActions,
  rightActions,
  columnLabels,
  columnTypes,
  featureDetails,
  onFeatureDetailsFetch,
  onFeatureDetailsClear,
  initialSettings,
  onConfigChange
}) => {
  const {t} = useTranslation();

  const theme = useTheme();

  const {isFullscreen, handleFullscreen} = useFullscreen();

  const language = useSelector(languageSelector);

  const nodes = useSelector(hubNodesSelector);
  const node = useSelector(nodeSelector);
  const selectedNode = nodeId ? nodes?.find(node => node.nodeId === nodeId) : node;

  const mapId = useMemo(() => `uuid__${uuidv4()}`, []);

  const [isFirstRender, setIsFirstRender] = useState(true);

  const [mapInitialized, setMapInitialized] = useState(false);

  const [mapSettings, setMapSettings] = useState({
    ...DEFAULT_GEOJSON_MAP_SETTINGS,
    pointMapType: pointMapTypeFromConfig,
    ...(initialSettings ?? {})
  });

  const [identifyResults, setIdentifyResults] = useState(null);
  const [identifyVisibility, setIdentifyVisibility] = useState(false);

  const [hoverResult, setHoverResult] = useState(null);

  const isPointData = useMemo(() => {
    const firstFeature = geoJson?.features?.[0];

    if (!firstFeature?.geometry?.type) return null;

    return ["Point", "MultiPoint"].includes(firstFeature.geometry.type);
  }, [geoJson]);

  const pointMapType = useMemo(() => mapSettings?.pointMapType, [mapSettings]);

  const timeFormatMap = useMemo(() => {
    const parsedExtras = EXTRAS.parse(selectedNode?.extras);

    return {
      [DIMENSION_TYPE.dateTime]: parsedExtras?.TimePeriodFormatDate?.[language],
      [DIMENSION_TYPE.time]: parsedExtras?.TimePeriodFormatTime?.[language],
      [DIMENSION_TYPE.day]: parsedExtras?.TimePeriodFormatDay?.[language],
      [DIMENSION_TYPE.month]: parsedExtras?.TimePeriodFormatMonth?.[language],
      [DIMENSION_TYPE.year]: parsedExtras?.TimePeriodFormatYear?.[language],
      [DIMENSION_TYPE.quarter]: parsedExtras?.TimePeriodFormatQuarter?.[language],
      [DIMENSION_TYPE.quadrimester]: parsedExtras?.TimePeriodFormatQuadrimester?.[language],
      [DIMENSION_TYPE.semester]: parsedExtras?.TimePeriodFormatSemester?.[language]
    };
  }, [selectedNode, language]);

  const showIdentifyDialog = useCallback(identifyResult => {
    setIdentifyVisibility(true);
    setIdentifyResults(identifyResult);
  }, []);

  const hideIdentifyDialog = useCallback(() => {
    setIdentifyVisibility(false);
    window.LMap.highlightFeatures(mapId, []);
    window.LMap.clearSelectedFeatures(mapId);
    setIdentifyResults(null);
  }, [mapId]);

  const highlightFeatures = useCallback(
    features => {
      window.LMap.highlightFeatures(mapId, features);
    },
    [mapId]
  );

  useEffect(() => {
    if (isPointData === null) return;

    const {layers, baseLayer} = getLayerOptions(
      mapLayer,
      defaultBaseLayer,
      mapLayersConfig,
      defaultLanguage,
      languages
    );

    const mapOptions = {
      showZoom: true,
      showScale: false,
      baseLayer: baseLayer,
      layers: layers,
      defaultExtent: defaultExtent,
      showBaseLayerRadio: !disableBaseLayer && mapLayer === MAP_LAYERS_ALL && (layers || []).length > 1,
      showSelection: true,
      disableWheelZoom: disableWheelZoom,
      isBaseLayerRadioPopupClosable: true,
      isSimpleInteraction: true,
      infoTolerance: infoTolerance,
      legendFontFamily: "Roboto",
      primaryColor: theme.palette.text.primary,
      onIdentify: identifyResult => {
        let features = [];
        if (identifyResult) {
          const keys = Object.keys(identifyResult) || [];
          if (keys.length > 0) {
            const firstKey = keys[0];
            features = identifyResult[firstKey] || [];
          }
        }
        if (features.length > 0) {
          showIdentifyDialog(features);
        } else {
          showMessage(t("modules.viewerGeojsonMap.identifyDialog.noResults.label"), SNACKBAR_MESSAGE_SEVERITY_INFO);
          hideIdentifyDialog();
        }
      },
      onSelect: selectResult => {
        let features = [];
        if (selectResult) {
          const keys = Object.keys(selectResult) || [];
          if (keys.length > 0) {
            const firstKey = keys[0];
            features = selectResult[firstKey] || [];
          }
        }
        if (features.length > 0) {
          showIdentifyDialog(features);
        } else {
          showMessage(t("modules.viewerGeojsonMap.identifyDialog.noResults.label"), SNACKBAR_MESSAGE_SEVERITY_INFO);
          hideIdentifyDialog();
        }
      },
      onHover: hoverResult => {
        let result = null;
        if (hoverResult) {
          const keys = Object.keys(hoverResult) || [];
          if (keys.length > 0) {
            const firstKey = keys[0];
            result = hoverResult[firstKey][0];
          }
        }
        setHoverResult(result);
      }
    };

    const mapTranslations = getTranslationOptions(t);
    window.LMap.initMap(mapId, mapOptions, mapTranslations, () => setMapInitialized(true));

    return () => {
      setMapInitialized(false);
      window.LMap.destroyMap(mapId);
    };
  }, [
    mapId,
    defaultLanguage,
    languages,
    mapLayersConfig,
    mapLayer,
    defaultBaseLayer,
    defaultExtent,
    infoTolerance,
    disableBaseLayer,
    disableWheelZoom,
    showIdentifyDialog,
    hideIdentifyDialog,
    t,
    theme.palette.text.primary,
    isPointData
  ]);

  useEffect(() => {
    if (isPointData === null) return;

    if (mapInitialized && LMap.isInitialized(mapId)) {
      window.LMap.removeLayer(mapId);

      hideIdentifyDialog();

      const geoJsonLayerOptions = {
        disableZoomToLayer: !isFirstRender,
        settings: {
          opacity: opacity
        },
        FillColor: fillColor,
        LineColor: borderColor,
        HighlightedFillColor: highlightedFillColor,
        HighlightedLineColor: highlightedBorderColor,
        InactiveFillColor: inactiveFillColor,
        InactiveLineColor: inactiveBorderColor,
        HoveredFillColor: hoveredFillColor,
        HoveredLineColor: hoveredLineColor,
        LineWidth: borderWidth,
        LineDash: borderDash,
        Radius: radius,
        Sides: sides,
        IconUrl: iconUrl,
        HeatmapGradient: heatmapGradient,
        HeatmapRadius: heatmapRadius,
        HeatmapBlur: heatmapBlur
      };

      if (isPointData && pointMapType === POINT_DATA_MAP_TYPE_CLUSTER) {
        window.LMap.addClusterLayer(mapId, geoJson, null, geoJsonLayerOptions);
      } else if (isPointData && pointMapType === POINT_DATA_MAP_TYPE_HEATMAP) {
        window.LMap.addHeatmapData(mapId, geoJson, null, geoJsonLayerOptions);
      } else {
        window.LMap.addGeoJsonLayer(mapId, geoJson, null, geoJsonLayerOptions);
      }
    }
  }, [
    mapId,
    geoJson,
    isFirstRender,
    pointMapType,
    opacity,
    fillColor,
    borderColor,
    highlightedFillColor,
    highlightedBorderColor,
    inactiveFillColor,
    inactiveBorderColor,
    hoveredFillColor,
    hoveredLineColor,
    borderDash,
    radius,
    borderWidth,
    sides,
    iconUrl,
    heatmapGradient,
    heatmapRadius,
    heatmapBlur,
    hideIdentifyDialog,
    mapInitialized,
    isPointData
  ]);

  return isPointData === null ? (
    <span />
  ) : (
    <Box
      className="viewer-geojson-map__wrapper"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px 16px 16px"
      }}
    >
      <Grid container justifyContent="space-between" alignItems="flex-start" className="viewer-geojson-map__header">
        <Grid className="viewer-geojson-map__header__title" size="grow">
          {showTitle && (title || "").length > 0 ? (
            <Typography
              variant="h6"
              className="viewer-geojson-map__header__title__text"
              sx={{display: "inline", verticalAlign: "middle"}}
            >
              {title}
            </Typography>
          ) : (
            <Box sx={{padding: "0", marginLeft: "8px"}}>{leftActions}</Box>
          )}
        </Grid>
        <Grid
          display="flex"
          alignItems="center"
          wrap="nowrap"
          columnSpacing={1}
          className="viewer-geojson-map__header__actions"
          sx={{marginRight: "8px"}}
        >
          {(infoMessage || "").length > 0 && (
            <Grid className="viewer-geojson-map__header__actions__info-message">
              <Tooltip title={infoMessage} placement="bottom">
                <InfoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    display: "inline",
                    verticalAlign: "middle",
                    margin: "8px"
                  }}
                />
              </Tooltip>
            </Grid>
          )}
          {rightActions}
          {!disableFullscreen && (
            <Grid className="viewer-geojson-map__header__actions__fullscreen-btn">
              <Tooltip
                title={
                  isFullscreen
                    ? t("modules.viewerGeojsonMap.actions.fullscreenBtn.exit")
                    : t("modules.viewerGeojsonMap.actions.fullscreenBtn.enter")
                }
                placement="bottom"
              >
                <IconButton onClick={handleFullscreen}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Box
        className="viewer-geojson-map"
        sx={{
          width: "100%",
          height: "100%",
          position: "relative"
        }}
      >
        {isPointData && (
          <Box
            className="viewer-geojson-map__point-data-selector"
            sx={{
              position: "absolute",
              zIndex: 1,
              top: "8px",
              left: "8px",
              padding: "8px",
              background: "rgba(255, 255, 255, 0.8)"
            }}
          >
            <TextField
              className="viewer-geojson-map__point-data-selector__select"
              select
              label={t("modules.viewerGeojsonMap.pointDataSelector.label")}
              value={pointMapType}
              onChange={ev => {
                const selectedPointMapType = ev.target.value;
                const nextMapSettings = {
                  ...mapSettings,
                  pointMapType: selectedPointMapType
                };

                setMapSettings(nextMapSettings);
                if (onConfigChange) onConfigChange({mapSettings: nextMapSettings});
                setIsFirstRender(false);
              }}
            >
              {(getPointDataSelectorOptions(t) || []).map((val, idx) => (
                <MenuItem
                  className={`viewer-geojson-map__point-data-selector__select__values__${val.value}`}
                  key={idx}
                  value={val.value}
                >
                  {val.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {hoverResult && (
          <Box
            className="viewer-geojson-map__hover-data-tooltip"
            sx={{
              position: "absolute",
              zIndex: 1,
              top: "8px",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "8px",
              background: "rgba(255, 255, 255, 0.8)"
            }}
          >
            {getLabelField(hoverResult, labelField, labelFieldList)}
          </Box>
        )}

        <Box
          id={mapId}
          className={`viewer-geojson-map__lmap-container ${isPointData ? "viewer-geojson-map--is-point-data" : ""}`}
          sx={{
            width: "100%",
            height: "100%"
          }}
        />

        <Box
          className="viewer-geojson-map__identify-results-container"
          sx={{
            display: identifyVisibility ? "block" : "none",
            position: "absolute",
            zIndex: 1,
            width: "400px",
            height: "100%",
            top: 0,
            right: 0
          }}
        >
          {(identifyResults ?? []).length > 0 && (
            <Card
              className="viewer-geojson-map__identify-results-container__card"
              sx={{
                width: "100%",
                height: "100%",
                padding: "8px",
                backgroundColor: "#f5f5f5"
              }}
            >
              <CardHeader
                className="viewer-geojson-map__identify-results-container__card__card-header"
                title={t("modules.viewerGeojsonMap.identifyDialog.title", {count: identifyResults?.length})}
                action={
                  <IconButton
                    onClick={hideIdentifyDialog}
                    aria-label={t("modules.viewerGeojsonMap.identifyDialog.close")}
                  >
                    <CloseIcon />
                  </IconButton>
                }
                slotProps={{
                  title: {
                    variant: "h6",
                    sx: {
                      fontSize: "16px"
                    }
                  }
                }}
                sx={{
                  padding: "12px"
                }}
              />
              <Box
                className="viewer-geojson-map__identify-results__card__content"
                sx={{
                  height: "calc(100% - 56px)",
                  overflow: "auto"
                }}
              >
                <IdentifyResults
                  identifyResults={identifyResults}
                  onSelect={highlightFeatures}
                  labelField={labelField}
                  labelFieldList={labelFieldList}
                  columnLabels={columnLabels}
                  columnTypes={columnTypes}
                  timeFormatMap={timeFormatMap}
                  featureDetails={featureDetails}
                  onFeatureDetailsFetch={onFeatureDetailsFetch}
                  onFeatureDetailsClear={onFeatureDetailsClear}
                />
              </Box>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const GeoJsonMap = props => (
  <FullscreenProvider>
    <GeoJsonMapComponent {...props} />
  </FullscreenProvider>
);

export default connect(mapStateToProps)(GeoJsonMap);
